<template>
  <div class="space-y-6">
    <form class="card-modern space-y-6" @submit.prevent="addOrigin">
      <div>
        <h2 class="text-2xl font-bold text-gray-900">Вбудовування форми</h2>
        <p class="mt-2 text-sm text-gray-600">
          Форму можна показати через iframe лише на доменах із списку нижче. Порожній список означає, що вбудувати її на інший сайт не можна.
          Додайте повний origin, наприклад https://client.com. Адреса з www і без www — це різні сайти.
        </p>
      </div>

      <div>
        <label for="embedOrigin" class="block text-sm font-medium text-gray-700 mb-2">
          Домен сайту
        </label>
        <div class="flex flex-col gap-3 sm:flex-row sm:items-center">
          <input
            id="embedOrigin"
            v-model="draftOrigin"
            type="text"
            inputmode="url"
            autocomplete="off"
            spellcheck="false"
            class="admin-input min-w-0 sm:w-auto sm:flex-1"
            placeholder="https://client.com"
          />
          <button
            type="submit"
            class="btn-hero w-fit shrink-0 self-start whitespace-nowrap"
            :disabled="saving || !draftOrigin.trim()"
          >
            {{ saving ? 'Додавання...' : 'Додати домен' }}
          </button>
        </div>
      </div>

      <div v-if="message" class="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-3">
        {{ message }}
      </div>
      <div v-if="error" class="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
        {{ error }}
      </div>
    </form>

    <div class="card-modern space-y-4">
      <h3 class="text-lg font-semibold text-gray-900">Дозволені сайти</h3>
      <p v-if="loading" class="text-sm text-gray-500">Завантаження...</p>
      <p v-else-if="origins.length === 0" class="text-sm text-gray-600">
        Список порожній. Форма відкривається лише на цьому сайті.
      </p>
      <ul v-else class="divide-y divide-gray-200">
        <li
          v-for="origin in origins"
          :key="origin"
          class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 py-3"
        >
          <code class="text-sm text-gray-800 break-all">{{ origin }}</code>
          <button
            type="button"
            class="text-red-600 hover:text-red-800 transition-colors duration-200 flex items-center gap-2 flex-shrink-0 py-2 px-3 rounded-lg hover:bg-red-50"
            :disabled="saving"
            @click="removeOrigin(origin)"
          >
            Прибрати
          </button>
        </li>
      </ul>
    </div>

    <div class="card-modern space-y-4">
      <div>
        <h3 class="text-lg font-semibold text-gray-900">Код для сайту клієнта</h3>
        <p class="mt-2 text-sm text-gray-600">
          {{ embedHint }} Висоту можна змінити, якщо форма не вміщується.
        </p>
      </div>
      <textarea
        class="admin-textarea font-mono text-sm"
        rows="8"
        readonly
        :value="iframeCode"
      ></textarea>
      <div class="flex flex-col sm:flex-row sm:items-center gap-3">
        <button type="button" class="btn-ghost" @click="copyCode">
          Скопіювати код
        </button>
        <span v-if="copyMessage" class="text-sm text-gray-600">{{ copyMessage }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import api from '@/utils/axios'

const origins = ref<string[]>([])
const draftOrigin = ref('')
const loading = ref(false)
const saving = ref(false)
const message = ref('')
const error = ref('')
const copyMessage = ref('')

const PUBLIC_FORM_URL = 'https://smartthings.evergreen.com.ua/'

const iframeSrc = computed(() => {
  const { hostname, origin } = window.location
  const isLocal = hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]'
  return isLocal ? PUBLIC_FORM_URL : `${origin}/`
})

const embedHint = computed(() => {
  if (origins.value.length === 0) {
    return 'Вставте цей код на сайт клієнта, наприклад https://client.com. Адреса у src — сторінка форми. Поки список доменів порожній, на чужому сайті вона не відкриється.'
  }
  return `Вставте цей код на ${origins.value.join(', ')}. Адреса у src — сторінка форми.`
})

const iframeCode = computed(() => {
  return [
    '<iframe',
    `  src="${iframeSrc.value}"`,
    '  title="Samsung SmartThings"',
    '  width="100%"',
    '  height="1600"',
    '  style="border:0;width:100%;max-width:100%;"',
    '></iframe>'
  ].join('\n')
})

const loadOrigins = async () => {
  loading.value = true
  error.value = ''
  try {
    const response = await api.get('/api/embed-origins')
    origins.value = Array.isArray(response.data?.origins) ? response.data.origins : []
  } catch (loadError: any) {
    error.value = loadError.response?.data?.message || 'Не вдалося завантажити список доменів'
  } finally {
    loading.value = false
  }
}

const addOrigin = async () => {
  message.value = ''
  error.value = ''
  copyMessage.value = ''
  saving.value = true
  try {
    const response = await api.post('/api/embed-origins', { origin: draftOrigin.value })
    origins.value = Array.isArray(response.data?.origins) ? response.data.origins : origins.value
    draftOrigin.value = ''
    message.value = 'Домен додано. Форму вже можна вбудувати на цьому сайті.'
  } catch (saveError: any) {
    error.value = saveError.response?.data?.message || 'Не вдалося додати домен'
  } finally {
    saving.value = false
  }
}

const removeOrigin = async (origin: string) => {
  if (!confirm(`Прибрати ${origin} зі списку?`)) return
  message.value = ''
  error.value = ''
  copyMessage.value = ''
  saving.value = true
  try {
    const response = await api.delete('/api/embed-origins', { data: { origin } })
    origins.value = Array.isArray(response.data?.origins) ? response.data.origins : origins.value
    message.value = 'Домен прибрано.'
  } catch (deleteError: any) {
    error.value = deleteError.response?.data?.message || 'Не вдалося прибрати домен'
  } finally {
    saving.value = false
  }
}

const copyCode = async () => {
  copyMessage.value = ''
  try {
    await navigator.clipboard.writeText(iframeCode.value)
    copyMessage.value = 'Код скопійовано'
  } catch {
    copyMessage.value = 'Не вдалося скопіювати. Виділіть код вручну.'
  }
}

onMounted(loadOrigins)
</script>
